import React, { Component } from 'react';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';

import StyleHelper from '../../shared/helpers/StyleHelper';
import FacilityHelper from '../helpers/FacilityHelper';
import Tooltip from './Tooltip.jsx';

import './SlotList.css';
import { isScrolledToBottom } from '../../shared/utils/scroll';
import { renderPricingBox } from './SecondRowPricingBox';
import PriceHelper from '../../shared/helpers/PriceHelper';

class SlotList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      /** @type {string[]} */
      show: [],
      tooltip: false,
    };
    this.listScroll = false;
  }

  /**
   * @param {string} identifier
   */
  toggleMore(identifier) {
    const show = this.state.show.slice();
    const index = show.indexOf(identifier);
    if (index < 0) {
      show.push(identifier);
    } else {
      show.splice(index, 1);
    }
    this.setState({
      show,
    });
  }

  componentDidUpdate() {
    if (typeof this.props.didUpdate === 'function') {
      this.props.didUpdate();
    }
    this.attachListScroll();
    this.handleScroll();
  }

  componentDidMount() {
    this.scrollFunction = () => {
      // @ts-expect-error
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => {
        this.handleScroll();
      }, 100);
    };
    window.addEventListener('scroll', this.scrollFunction);
    this.attachListScroll();
    this.firstScrollHandle = setTimeout(() => {
      this.handleScroll();
    }, 500);
  }

  componentWillUnmount() {
    // @ts-expect-error
    clearTimeout(this.scrollTimer);
    // @ts-expect-error
    clearTimeout(this.firstScrollHandle);
    // @ts-expect-error
    window.removeEventListener('scroll', this.scrollFunction);
    const node = this.list;
    if (node) {
      // @ts-expect-error
      node.removeEventListener('scroll', this.scrollFunction);
    }
  }

  attachListScroll() {
    if (!this.listScroll) {
      const node = this.list;
      if (node) {
        // @ts-expect-error
        node.addEventListener('scroll', this.scrollFunction);
        this.listScroll = true;
      }
    }
  }

  isScrolledToBottom() {
    return isScrolledToBottom(this.list);
  }

  handleScroll() {
    // do nothing
    const isAtBottom = this.isScrolledToBottom();
    if (!isAtBottom && !this.state.tooltip) {
      this.setState({ tooltip: true });
    } else if (isAtBottom && this.state.tooltip) {
      this.setState({ tooltip: false });
    }
  }

  render() {
    const { facility, slots } = this.props;
    const filter = this.props.filter || {};
    const slotList = [];
    let hasPriceCol = false;
    let hasBookSpaceCol = false;
    let index = 0;

    const branded = StyleHelper.applyCustomStyle(
      this.props.customStyle,
      '.SlotList>li span.slotBookSpace a.button.branded'
    )
      ? 'branded'
      : '';

    StyleHelper.applyCustomStyle(
      this.props.memberBookButtonStyle,
      '.SlotList>li span.slotBookSpace a.button.memberBook.branded'
    );

    // Filters slots not in the lookahead period and then sorts them by start date
    const slotsFilteredAndSorted = FacilityHelper.sortSlotsByStartDate(
      FacilityHelper.filterSlotsByLookahead(
        slots,
        this.props.date,
        this.props.numberOfDaysLookahead
      )
    );

    const provider = facility['imin:dataSource'].identifier;
    for (const slot of slotsFilteredAndSorted) {
      const isInFilter = FacilityHelper.isInFilter(facility, filter);
      if (isInFilter) {
        const dateString = DateTime.fromISO(slot.startDate).toLocaleString({
          weekday: 'short',
          day: 'numeric',
          month: 'short',
        });
        const startTimeString = DateTime.fromISO(slot.startDate).toLocaleString(
          DateTime.TIME_24_SIMPLE
        );
        let endTime = DateTime.fromISO(slot.endDate);
        if (endTime.second === 59) {
          endTime = endTime.plus({ seconds: 1 });
        }

        const endTimeString = endTime.toLocaleString(DateTime.TIME_24_SIMPLE);
        const uniqueId = `${facility.identifier}-${slot.startDate}-${index}`;
        let activityList = [];
        if (facility.activity instanceof Array && facility.activity.length) {
          let activityIndex = 0;
          for (const activity of facility.activity) {
            if (activity.prefLabel) {
              activityList.push(<li key={activityIndex}>{activity.prefLabel}</li>);
            }
            activityIndex += 1;
          }
        }

        const price = PriceHelper.getPricesList(
          slot['imin:aggregateOffer'] || facility['imin:aggregateOffer'],
          provider
        );
        if (price.opportunityPrice) {
          hasPriceCol = true;
        }

        const slotBookSpaceObjects = FacilityHelper.slotBookSpaceObjects({
          slot: slot,
          facilityUse: facility,
          baseCheckoutUrl: this.props.checkoutUrl,
          branded: branded,
          checkoutTheme: this.props.checkoutTheme,
          bookableProviders: this.props.bookableProviders,
          provider: provider,
          shouldShowBookAsGuestButton: this.props.shouldShowBookAsGuestButton,
          shouldShowBookAsMemberButton: this.props.shouldShowBookAsMemberButton,
          memberDeepLinks: this.props.memberDeepLinks,
          shouldShowNoAvailability: this.props.shouldShowNoAvailability,
        });
        const slotBookSpaceElements = slotBookSpaceObjects.map((obj, i) => {
          const TagName = obj.tag;
          const slotBookSpace = (
            <TagName className={obj.className} {...obj.attributes} key={`slotBookSpace-${i}`}>
              {obj.text}
            </TagName>
          );
          return slotBookSpace;
        });
        const slotBookSpace = <span className="slotBookSpace">{slotBookSpaceElements}</span>;

        if (slotBookSpaceElements.length > 0) {
          hasBookSpaceCol = true;
        }

        const hasSecondRow =
          facility.description !== null ||
          facility.description !== undefined ||
          price.opportunityPrice;

        slotList.push(
          <li
            key={index}
            className={`${index % 2 === 0 ? 'odd' : 'even'} ${hasSecondRow ? '' : 'noDescription'}`}
          >
            <span className="slotTimes">
              <span>{dateString}</span>
              <span>
                {startTimeString} -&nbsp;{endTimeString}
              </span>
            </span>
            <span className="slotName" onClick={() => this.toggleMore(uniqueId)}>
              {facility.name}
            </span>
            <span className="slotMore" onClick={() => this.toggleMore(uniqueId)}>
              <img alt="More Information" src="info.png"></img>
            </span>
            <span className="slotPrice">{price.opportunityPrice}</span>
            {!this.props.shouldShowNoAvailability && slotBookSpace}
          </li>
        );

        if (hasSecondRow && this.state.show.indexOf(uniqueId) >= 0) {
          let bookButtonAvailability = <span className="slotAvailability placeHolder"></span>;

          if (slotBookSpaceObjects[0].availability) {
            bookButtonAvailability = (
              <span className="slotAvailability">
                <strong>{slotBookSpaceObjects[0].availability}</strong>
              </span>
            );
          }
          slotList.push(
            <li
              key={`${index}-description`}
              className={`description ${index % 2 === 0 ? 'odd' : 'even'}`}
            >
              <span className="slotDescription">
                {FacilityHelper.description(facility.description)}
              </span>
              {renderPricingBox(price, this.props.showPrices)}
              {bookButtonAvailability}
            </li>
          );
        }

        index += 1;
      }
    }

    if (slotList.length === 0) {
      this.listScroll = false;
      return (
        <div className="NoResults">
          <span>
            <p>There are no slots available with the current filters.</p>
            <p>Try adjusting the filters to see what else is available.</p>
          </span>
        </div>
      );
    }

    const style = {
      paddingTop: this.props.paddingTop,
    };

    if (this.props.height) {
      style.maxHeight = `${this.props.height - 10.5}rem`;
    }

    let tooltip;
    if (this.state.tooltip) {
      tooltip = <Tooltip title="Scroll down for more slots" />;
    }

    return (
      <div>
        <ul
          style={style}
          ref={(list) => (this.list = list)}
          className={`SlotList ${hasPriceCol ? 'hasPrices' : ''} ${
            hasBookSpaceCol ? 'hasBookSpaces' : ''
          }`}
        >
          {slotList}
        </ul>
        {tooltip}
      </div>
    );
  }
}

SlotList.propTypes = {
  didUpdate: PropTypes.func,
  facility: PropTypes.object,
  slots: PropTypes.array,
  filter: PropTypes.object,
  customStyle: PropTypes.object,
  memberBookButtonStyle: PropTypes.object,
  checkoutUrl: PropTypes.string,
  baseMemberBookUrl: PropTypes.string,
  checkoutTheme: PropTypes.string,
  paddingTop: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.number,
  bookableProviders: PropTypes.array,
  shouldShowBookAsGuestButton: PropTypes.bool,
  shouldShowBookAsMemberButton: PropTypes.bool,
  memberDeepLinks: PropTypes.object,
  shouldShowNoAvailability: PropTypes.bool,
  showPrices: PropTypes.bool,
  numberOfDaysLookahead: PropTypes.number,
  date: PropTypes.string,
};

export default SlotList;
